import Tooltip from '@/components/ui/Tooltip';
import type { ProjectionGrid, FundingLevel } from '@/utils/retirementCalculations';
import { RETURN_RATE_LABELS } from '@/utils/retirementCalculations';

interface PublicHeatmapProps {
  grid: ProjectionGrid;
}

/**
 * Simplified heatmap for public calculator
 * WP-LANDING-3, WP-RETIRE-5: Qualitative labels + 3-level funding
 */
export function PublicHeatmap({ grid }: PublicHeatmapProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="p-3 text-left text-sm font-semibold text-gray-600 bg-gray-50 border-b border-gray-200">
              Return Scenario
            </th>
            {grid.ageColumns.map((age) => (
              <th
                key={age}
                className="p-3 text-center text-sm font-semibold text-gray-600 bg-gray-50 border-b border-gray-200"
              >
                Age {age}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.cells.map((row, rowIndex) => {
            const rate = grid.returnRates[rowIndex] ?? 0;
            const rateLabel = RETURN_RATE_LABELS[rate];

            return (
              <tr key={rate}>
                <td className="p-3 text-sm font-medium text-gray-700 border-b border-gray-100">
                  {rateLabel ? (
                    <Tooltip content={rateLabel.tooltip} position="right">
                      <span className="cursor-help border-b border-dotted border-gray-400">
                        {rateLabel.label}
                      </span>
                    </Tooltip>
                  ) : (
                    `${Math.round(rate * 100)}% return`
                  )}
                </td>
                {row.map((cell) => (
                  <td
                    key={`${cell.returnRate}-${cell.age}`}
                    className={`p-3 text-center text-sm font-semibold border-b border-gray-100 ${getFundingColorClasses(cell.fundingLevel)}`}
                  >
                    {formatCurrency(cell.projectedWealthCents)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Legend - 4 items (WP-RETIRE-5, WP-RETIRE-11) */}
      <div className="mt-4 flex flex-wrap justify-center gap-4 sm:gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200" />
          <span className="text-gray-600">Pre-Retirement Projected Net Worth</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-100 border border-green-200" />
          <span className="text-gray-600">Well Funded</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-200" />
          <span className="text-gray-600">Adequate</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-100 border border-red-200" />
          <span className="text-gray-600">Underfunded</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Get Tailwind classes for funding level colors
 * 3-level system (WP-RETIRE-5)
 */
function getFundingColorClasses(fundingLevel: FundingLevel): string {
  switch (fundingLevel) {
    case 'pre_retirement':
      return 'bg-blue-50 text-blue-800';
    case 'well_funded':
      return 'bg-green-50 text-green-800';
    case 'adequate':
      return 'bg-yellow-50 text-yellow-800';
    case 'underfunded':
      return 'bg-red-50 text-red-800';
    default:
      return 'bg-gray-50 text-gray-800';
  }
}

/**
 * Format cents to currency display
 */
function formatCurrency(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000000) {
    return `$${(dollars / 1000000).toFixed(1)}M`;
  }
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(0)}K`;
  }
  return `$${dollars.toFixed(0)}`;
}

export default PublicHeatmap;
